/* Last-resort readability for legacy/dynamically injected filled CTAs.
 * Keep existing destinations, handlers, disabled semantics and background colors.
 * Explicit brand CSS is primary; this guard only repairs measured low contrast. */
(() => {
  'use strict';
  if (window.__AMCActionContrastV2) return;
  window.__AMCActionContrastV2 = true;
  const selector = 'a[class*="btn"],a[class*="button"],a[class*="primary"],a[class*="pill"],a[class*="tab"],a[class*="action"],a[class*="cta"],a[aria-current],button,[role="button"]';
  const excluded = '#PBarNextFrame,.shopify-payment-button,.quantity,.quantity-input,.swatch,.uspe-pc__swatch,.uspe-pc__option,.product__media,.amc-hdr__icons,[data-swatch],.shopify-pc__banner__btn-manage-prefs';
  const white = [255,255,255], navy = [11,27,51];
  const rgb = value => {
    if (!/^rgba?\(/.test(value || '')) return null;
    const n=value.match(/[\d.]+/g).map(Number);
    return {v:n.slice(0,3),a:n.length>3?n[3]:1};
  };
  const luminance = c => c.map(v=>v/255).map(v=>v<=.04045?v/12.92:((v+.055)/1.055)**2.4).reduce((n,v,i)=>n+v*[.2126,.7152,.0722][i],0);
  const contrast = (a,b) => {const x=luminance(a),y=luminance(b);return (Math.max(x,y)+.05)/(Math.min(x,y)+.05);};
  const setText = (el,color) => {
    if(el.style.getPropertyValue('color')!==color||el.style.getPropertyPriority('color')!=='important')el.style.setProperty('color',color,'important');
    if(el.style.getPropertyValue('-webkit-text-fill-color')!==color||el.style.getPropertyPriority('-webkit-text-fill-color')!=='important')el.style.setProperty('-webkit-text-fill-color',color,'important');
  };
  const inspect = el => {
    if (!(el instanceof HTMLElement) || el.closest(excluded) || !el.getClientRects().length || el.textContent.trim().length<2) return;
    if(el.matches('.visually-hidden,.sr-only')&&!el.matches(':focus-visible'))return;
    const s=getComputedStyle(el), bg=rgb(s.backgroundColor);
    // No assumptions about transparent controls, gradient/image backgrounds or pseudo-element buttons.
    if(!bg||bg.a<.98||s.backgroundImage!=='none')return;
    const fg=rgb(s.webkitTextFillColor)||rgb(s.color);
    const textNodes=[el,...el.querySelectorAll('span,strong,b,small')].filter(n=>n.textContent.trim().length>1&&(n===el||!n.closest('.badge,.price,.visually-hidden,.sr-only')));
    const bad=textNodes.some(n=>{const t=getComputedStyle(n),c=rgb(t.webkitTextFillColor)||rgb(t.color);return c&&contrast(c.v,bg.v)<4.5;});
    if(!bad&&(!fg||contrast(fg.v,bg.v)>=4.5))return;
    const color=contrast(white,bg.v)>=contrast(navy,bg.v)?'rgb(255, 255, 255)':'rgb(11, 27, 51)';
    return {el,textNodes,color};
  };
  let queued=false;
  const dirty=new Set(), ownStyles=new WeakMap();
  const mark=root=>{
    if(!(root instanceof HTMLElement))return;
    dirty.add(root);
    schedule();
  };
  const audit=()=>{
    queued=false;
    const candidates=new Set(), inputs=new Set();
    dirty.forEach(root=>{
      if(!root.isConnected)return;
      if(root.matches(selector))candidates.add(root);
      root.querySelectorAll(selector).forEach(el=>candidates.add(el));
      root.querySelectorAll('[data-ame-parts] input[name="quantity"]').forEach(el=>inputs.add(el));
    });
    dirty.clear();
    // Finish every computed-style/layout read before applying any repair.
    const plans=Array.from(candidates,inspect).filter(Boolean);
    plans.forEach(({el,textNodes,color})=>{
      textNodes.forEach(n=>{
        setText(n,color);
        ownStyles.set(n,n.getAttribute('style'));
      });
      el.dataset.amcContrastRepaired='true';
    });
    inputs.forEach(input=>{
      const title=input.closest('[data-ame-parts]')?.dataset.productTitle;
      if(title&&/ProductDrop/.test(input.getAttribute('aria-label')||''))input.setAttribute('aria-label','Quantity for '+title);
    });
  };
  // Contrast checks are non-geometric. Read after the browser's paint rather
  // than forcing a whole-page style/layout flush inside its pre-paint rAF.
  const schedule=()=>{if(!queued){queued=true;requestAnimationFrame(()=>setTimeout(audit,0));}};
  const start=()=>{
    mark(document.body);
    new MutationObserver(records=>records.forEach(record=>{
      if(record.type==='attributes'){
        if(record.attributeName==='style'&&ownStyles.get(record.target)===record.target.getAttribute('style'))return;
        const control=record.target.closest?.(selector);
        if(control){mark(control);return;}
        if(record.attributeName==='style'){
          const styles=(record.oldValue||'')+';'+(record.target.getAttribute('style')||'');
          if(!/(?:^|;)\s*(?:background(?:-color)?|color|filter|opacity|--[\w-]*(?:color|background|foreground)[\w-]*)\s*:/i.test(styles))return;
        }else if(record.attributeName==='class'){
          const old=new Set((record.oldValue||'').split(/\s+/)),now=new Set(record.target.classList);
          const changes=[...old,...now].filter(token=>old.has(token)!==now.has(token));
          if(changes.length&&changes.every(token=>/^(?:overflow-hidden|prevent-scrolling|no-scroll|scroll-locked|is-scrolling)$/.test(token)))return;
        }
        mark(record.target);
      }else{
        record.addedNodes.forEach(node=>{if(node.nodeType===1)mark(node);});
        const control=record.target.closest?.(selector);
        if(control)mark(control);
      }
    })).observe(document.body,{subtree:true,childList:true,attributes:true,attributeOldValue:true,attributeFilter:['class','style','disabled']});
    ['pointerover','focusin'].forEach(event=>document.addEventListener(event,e=>{
      const el=e.target.closest?.(selector);
      if(el&&el!==e.relatedTarget?.closest?.(selector))mark(el);
    },{passive:true}));
    let lastWidth=window.innerWidth;
    window.addEventListener('resize',()=>{
      // Mobile browsers fire resize when the toolbar shows or hides during scroll. Only a width change can alter contrast.
      if(window.innerWidth===lastWidth)return;
      lastWidth=window.innerWidth;
      mark(document.body);
    },{passive:true});
    window.addEventListener('load',()=>mark(document.body),{once:true});
    document.addEventListener('shopify:section:load',e=>mark(e.target));
    document.addEventListener('keydown',e=>{
      if(e.key!=='Escape')return;
      const header=document.querySelector('.amc-hdr');
      if(!header)return;
      header.querySelectorAll('.amc-hdr__item.has-mega').forEach(item=>{
        const panel=item.querySelector('.amc-hdr__mega');
        if(!panel||getComputedStyle(panel).visibility!=='visible')return;
        const link=item.querySelector('.amc-hdr__link');
        if(link)link.focus({preventScroll:true});
        item.setAttribute('data-amc-dismissed','');
      });
    },true);
  };
  // Last-resort guard: start once the browser is idle so it does not compete with first render.
  const startLater=()=>('requestIdleCallback' in window)?requestIdleCallback(start,{timeout:2000}):setTimeout(start,300);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',startLater,{once:true});else startLater();
})();